import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Sidebar from '../components/Sidebar';
import './QuotationStyles.css';

const API = 'https://cctvapi.shopkwality.in/api';

const normalizeItems = (items = []) =>
  items.map(i => ({
    ...i,
    gst_percent: i.gst_percent === undefined || i.gst_percent === null ? 18 : i.gst_percent
  }));

const GenerateQuotation = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [customerName, setCustomerName] = useState('');
  const [customerMobile, setCustomerMobile] = useState('');
  const [items, setItems] = useState([]);
  const [charges, setCharges] = useState('0');
  const [discount, setDiscount] = useState('0');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (location.state) {
      setCustomerName(location.state.customerName || '');
      setCustomerMobile(location.state.customerMobile || '');
      setCharges(location.state.charges || '0');
      setDiscount(location.state.discount || '0');
      
      if (location.state.items) {
        setItems(normalizeItems(location.state.items));
      }
    }
  }, [location.state]);

  const updateItem = (idx, key, value) => {
    setItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      
      if (key === 'name') {
        return { ...item, name: value };
      }
      
      if (key === 'gst_percent') {
        return { ...item, gst_percent: Math.min(28, Math.max(0, Number(value) || 0)) };
      }
      
      return { ...item, [key]: Math.max(0, Number(value) || 0) };
    }));
  };

  const removeItem = (idx) => {
    setItems(prev => prev.filter((_, i) => i !== idx));
  };

  const addCustomProduct = () => {
    setItems(prev => [...prev, {
      product_id: null,
      name: '',
      quantity: 1,
      unit_price: 0,
      gst_percent: 18
    }]);
  };

  const subtotal = items.reduce((s, i) => s + i.quantity * i.unit_price, 0);
  
  const totalGst = items.reduce((s, i) => {
    const base = i.quantity * i.unit_price;
    return s + (base * (i.gst_percent ?? 18)) / 100;
  }, 0);
  
  const total = subtotal + totalGst + Number(charges || 0) - Number(discount || 0);

  const createQuotation = async () => {
    if (!customerName.trim() || !customerMobile.trim()) {
      alert('Customer name & mobile are required');
      return;
    }

    if (items.length === 0) {
      alert('Add at least one product');
      return;
    }

    for (const i of items) {
      if (!i.product_id && !i.name.trim()) {
        alert('Custom product name is required');
        return;
      }
    }

    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/quotations/manual`,
        {
          customer_name: customerName.trim(),
          customer_mobile: customerMobile.trim(),
          charges: Number(charges),
          discount: Number(discount),
          items: items.map(i => ({
            product_id: i.product_id,
            product_name: i.name,
            quantity: i.quantity,
            unit_price: i.unit_price,
            gst_percent: i.gst_percent
          }))
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('Quotation created successfully!');
      navigate(`/quotations/${res.data.data.id}`);
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || 'Failed to create quotation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="dashboard-main">
        <div className="page-header-quotation">
          <div className="container">
            <h1 className="page-title">New Quotation</h1>
            <p className="page-subtitle">Create a quotation for customer</p>
          </div>
        </div>

        <div className="container">
          <form onSubmit={(e) => { e.preventDefault(); createQuotation(); }}>
            {/* Customer Information */}
            <div className="quotation-section">
              <h2 className="section-title">Customer Information</h2>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label required">Customer Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Enter customer name"
                    disabled={loading}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label required">Mobile Number</label>
                  <input
                    type="tel"
                    className="form-input"
                    value={customerMobile}
                    onChange={(e) => setCustomerMobile(e.target.value)}
                    placeholder="Enter mobile number"
                    disabled={loading}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Products Section */}
            <div className="quotation-section">
              <div className="section-header">
                <h2 className="section-title">Products</h2>
                <div className="section-actions">
                  <button
                    type="button"
                    onClick={() => navigate('/products/browse', { 
                      state: { 
                        from: 'quotation',
                        customerName,
                        customerMobile,
                        charges,
                        discount,
                        items
                      } 
                    })}
                    className="btn btn-secondary"
                    disabled={loading}
                  >
                    🗂️ Browse Products
                  </button>
                  <button
                    type="button"
                    onClick={addCustomProduct}
                    className="btn btn-success"
                    disabled={loading}
                  >
                    ➕ Add Custom
                  </button>
                </div>
              </div>

              {items.length === 0 ? (
                <div className="empty-state-inline">
                  <div className="empty-icon">📦</div>
                  <div className="empty-text">No products added</div>
                  <div className="empty-subtext">Browse products or add custom items</div>
                </div>
              ) : (
                <div className="quotation-items">
                  {items.map((item, idx) => {
                    const base = item.quantity * item.unit_price;
                    const gst = (base * (item.gst_percent ?? 18)) / 100;
                    const lineTotal = base + gst;

                    return (
                      <div key={idx} className="quotation-item-card">
                        <div className="item-header">
                          {item.product_id ? (
                            <h4>{item.name}</h4>
                          ) : (
                            <input
                              type="text"
                              className="form-input"
                              placeholder="Product name *"
                              value={item.name}
                              onChange={(e) => updateItem(idx, 'name', e.target.value)}
                              disabled={loading}
                              required
                            />
                          )}
                          <button
                            type="button"
                            onClick={() => removeItem(idx)}
                            className="btn-remove"
                            disabled={loading}
                          >
                            ✕
                          </button>
                        </div>

                        <div className="item-grid">
                          <div className="form-group">
                            <label className="form-label">Quantity</label>
                            <input
                              type="number"
                              className="form-input"
                              value={item.quantity}
                              onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                              min="0"
                              step="1"
                              disabled={loading}
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Unit Price</label>
                            <input
                              type="number"
                              className="form-input"
                              value={item.unit_price}
                              onChange={(e) => updateItem(idx, 'unit_price', e.target.value)}
                              min="0"
                              step="0.01"
                              disabled={loading}
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label">GST %</label>
                            <input
                              type="number"
                              className="form-input"
                              value={item.gst_percent ?? 18}
                              onChange={(e) => updateItem(idx, 'gst_percent', e.target.value)}
                              min="0"
                              max="28"
                              disabled={loading}
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Line Total</label>
                            <div className="line-total-display">
                              ₹{lineTotal.toFixed(2)}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Additional Charges */}
            <div className="quotation-section">
              <h2 className="section-title">Additional Charges & Discount</h2>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Additional Charges</label>
                  <input
                    type="number"
                    className="form-input"
                    value={charges}
                    onChange={(e) => setCharges(e.target.value)}
                    min="0"
                    step="0.01"
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Discount</label>
                  <input
                    type="number"
                    className="form-input"
                    value={discount}
                    onChange={(e) => setDiscount(e.target.value)}
                    min="0"
                    step="0.01"
                    disabled={loading}
                  />
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="quotation-summary">
              <div className="summary-row">
                <span>Subtotal:</span>
                <span>₹{subtotal.toFixed(2)}</span>
              </div>
              <div className="summary-row">
                <span>GST:</span>
                <span>₹{totalGst.toFixed(2)}</span>
              </div>
              <div className="summary-row summary-total">
                <span>Total:</span>
                <span>₹{Math.round(total)}</span>
              </div>
            </div>

            <div className="form-actions">
              <button 
                type="submit" 
                disabled={loading} 
                className="btn btn-primary"
              >
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    Creating...
                  </>
                ) : (
                  'Generate Quotation'
                )}
              </button>
              <button 
                type="button" 
                onClick={() => navigate('/quotations')} 
                className="btn btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default GenerateQuotation;
